/**
 * Freeze V2 — Incomplete Embryos Dialog
 *
 * Shows a list of temp embryos that are missing required fields
 * (stage, grade, owner, freeze day, grader, TT for exports).
 * User can proceed anyway or cancel to fix them first.
 */
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export type IncompleteEmbryo = {
  plateNumber: number;
  embryoName: string;
  missingFields: string[];
};

type IncompleteEmbryosDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  incompleteEmbryos: IncompleteEmbryo[];
  onProceed: () => void;
  onCancel: () => void;
};

export const IncompleteEmbryosDialog = ({
  open,
  onOpenChange,
  incompleteEmbryos,
  onProceed,
  onCancel,
}: IncompleteEmbryosDialogProps) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Incomplete Embryos Found</DialogTitle>
          <DialogDescription>
            {incompleteEmbryos.length} embryo(s) are missing required fields.
            You can proceed with the update anyway or cancel to fix them.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <div className="max-h-64 space-y-1 overflow-y-auto">
            {incompleteEmbryos.map((emb, i) => (
              <div
                key={i}
                className="flex items-start gap-2 rounded border border-amber-200 bg-amber-50 p-2 text-sm dark:border-amber-800 dark:bg-amber-950"
              >
                <span className="font-medium text-amber-800 dark:text-amber-200">
                  Plate {emb.plateNumber}
                </span>
                <span className="text-gray-700 dark:text-gray-300">
                  {emb.embryoName}
                </span>
                <span className="ml-auto text-xs text-amber-600 dark:text-amber-400">
                  Missing: {emb.missingFields.join(', ')}
                </span>
              </div>
            ))}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            Cancel — Fix Issues
          </Button>
          <Button variant="destructive" onClick={onProceed}>
            Proceed Anyway
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
